import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Button, TextField } from "@mui/material";
import { getWellConfig } from "../../utils/wellsConfig";
import { ProgressDialog } from "./progressPopup";
import { PRC_WELL } from "../../utils/PRC_WELL";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function TimedIncrementalTab({
  timedIncrementalSettings,
  setLoading,
  selectedTab,
  selectedDevice,
  refreshData,
}) {
  const [timedIncrementalValues, setTimedIncrementalValues] = useState(
    timedIncrementalSettings
  );
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");

  useEffect(() => {
    setTimedIncrementalValues(timedIncrementalSettings);
  }, [timedIncrementalSettings]);

  const handleChange = (index, newValue) => {
    setTimedIncrementalValues((prev) =>
      prev.map((item, i) => (i === index ? { ...item, value: newValue } : item))
    );
  };

  const handleSubmit = async () => {
    // Step 1: Check if all fields are filled
    const isAnyFieldEmpty = timedIncrementalValues.some(
      (item) => item.value === ""
    );

    if (isAnyFieldEmpty) {
      console.error("❌ Please fill all fields.");
      toast.error("❌ Please fill all fields before submitting.");
      return;
    }
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 2: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 3: Give React a chance to render before doing heavy work
    setTimeout(() => {
      runDeviceUpdate();
    }, 100); // 100ms is enough to show the dialog
  };

  const runDeviceUpdate = async () => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let registerList = [
        wellConfig.AUTO_SP,
        wellConfig.CASING_ACTUAL,
        wellConfig.CASING_TIME_SAMPLE,
        wellConfig.SAMPLE_TIME,
        wellConfig.SAMPLE_DEADBAND,
        wellConfig.SETPOINT_INC_VAL,
        wellConfig.SETPOINT_DEC_VAL,
        wellConfig.MIN_FLOW_RATE,
        wellConfig.MAX_FLOW_RATE,
      ];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      const automaticSP = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Automatic SP"
      );
      const automaticSPValue = parseFloat(automaticSP?.value) * 10;

      const casingActual = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Casing PSI (Actual)"
      );
      const casingActualValue = parseFloat(casingActual?.value) * 10;

      const casingTimeSimple = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Casing PSI (@Time of Sample)"
      );
      const casingTimeSimpleValue = parseFloat(casingTimeSimple?.value) * 10;

      const sampleTime = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Sample Time SP"
      );
      const sampleTimeValue = sampleTime?.value;

      const sampleDeadband = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Sample Deadband"
      );
      const sampleDeadbandValue = parseFloat(sampleDeadband?.value) * 10;

      const setpointInc = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Setpoint Increment Value"
      );
      const setpointIncValue = parseFloat(setpointInc?.value) * 10;

      const setpointDec = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Setpoint Decrement Value"
      );
      const setpointDecValue = parseFloat(setpointDec?.value) * 10;

      const minFlowRate = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Minimum Allowed Flow Rate"
      );
      const minFlowRateValue = parseFloat(minFlowRate?.value) * 10;

      const maxFlowRate = PRC_WELL.getValueFromWell(
        timedIncrementalValues,
        "Maximum Allowed Flow Rate"
      );
      const maxFlowRateValue = parseFloat(maxFlowRate?.value) * 10;

      cmd += "," + (wellConfig.AUTO_SP - 40001) + "," + automaticSPValue;
      cmd += "," + (wellConfig.CASING_ACTUAL - 40001) + "," + casingActualValue;
      cmd +=
        "," +
        (wellConfig.CASING_TIME_SAMPLE - 40001) +
        "," +
        casingTimeSimpleValue;
      cmd += "," + (wellConfig.SAMPLE_TIME - 40001) + "," + sampleTimeValue;
      cmd +=
        "," + (wellConfig.SAMPLE_DEADBAND - 40001) + "," + sampleDeadbandValue;
      cmd +=
        "," + (wellConfig.SETPOINT_INC_VAL - 40001) + "," + setpointIncValue;
      cmd +=
        "," + (wellConfig.SETPOINT_DEC_VAL - 40001) + "," + setpointDecValue;
      cmd += "," + (wellConfig.MIN_FLOW_RATE - 40001) + "," + minFlowRateValue;
      cmd += "," + (wellConfig.MAX_FLOW_RATE - 40001) + "," + maxFlowRateValue;

      console.log("Sending command:", cmd);
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
         {unitId, cmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd }`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {unitId, cmd: pCmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd }`,},
            { headers: { id: "anything", "Content-Type": "application/json" } }
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          bgcolor: "#1e293b",
          px: 1,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            color: "#ffffffff",
            fontSize: 14,
          }}
        >
          Timed Incremental Setpoints
        </Typography>
        <Button
          color="primary"
          variant="contained"
          sx={{ fontSize: 12 }}
          size="small"
          onClick={() => handleSubmit()}
        >
          Submit
        </Button>
      </Box>
      <Box
        sx={{
          backgroundColor: "#374151",
          color: "#fff",
          py: 3,
          px: 1,
          width: "100%",
          maxWidth: 1000,
        }}
      >
        {timedIncrementalValues.map((item, index) => (
          <React.Fragment key={index}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #a0a0a0ff",
                py: 1.5,
              }}
            >
              <Typography
                sx={{
                  flex: 2,
                  color: "#ffffffff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {item.label}
              </Typography>
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flex: 1,
                }}
              >
                <Grid sx={{ flex: 1 }}>
                  <TextField
                    label={item?.measurement}
                    disabled={
                      item?.label === "Casing PSI (Actual)" ||
                      item?.label === "Casing PSI (@Time of Sample)"
                    }
                    fullWidth
                    size="small"
                    value={item.value}
                    onChange={(e) => handleChange(index, e.target.value)}
                    InputLabelProps={{ style: { color: "#fff", fontSize: 14 } }}
                    InputProps={{
                      sx: {
                        color: "#fff",
                        backgroundColor: "#1f2937",
                        padding: 0.2,
                        fontSize: 12,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            {/* Insert a new Box after the first item */}
            {index === 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderBottom: "1px solid #a0a0a0ff",
                  py: 1,
                  backgroundColor: "#f0f0f0", // Example background color
                }}
              >
                <Typography
                  sx={{
                    color: "#333",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Auto Adjust SP
                </Typography>
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>

      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await refreshData();
        }}
        loadingMessage="Check Device Response. Operation in-progress ..."
        successMessage="Successfully updated!"
      />
    </Box>
  );
}
